import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PageLoaderService } from '../../../util/pageloader.service';
import { finalize, map, switchMap, catchError, of, tap, EMPTY } from 'rxjs';

import * as LiveClassActions from './live-class.action';
import { LiveClassService } from './live-class.service';
import { Router } from '@angular/router';
import { Payment } from '../order/order.model';
import { HttpErrorResponse } from '@angular/common/http';
import { LiveClass } from './models/live-class';
import { Invoice } from '../../../models/invoice.model';
import {
  Subscription,
  SubscriptionStatus,
} from '../subscription/subscription.model';
import { ToastService } from '@black/ui';

@Injectable()
export class LiveClassEffects {
  private actions$ = inject(Actions);
  private liveClassService = inject(LiveClassService);
  private pageLoader = inject(PageLoaderService);
  private router = inject(Router);
  private toastService = inject(ToastService);

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveClassActions.createLiveClass.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.create(action.liveClassDTO).pipe(
          map((response) => {
            return LiveClassActions.createLiveClassSuccess({
              liveClass: response,
            });
          }),
          catchError((err) => {
            const message =
              (typeof err?.error === 'string'
                ? err.error
                : err?.error?.message || err?.error?.description) ||
              err?.message ||
              'Failed to create live class.';
            this.toastService.showToast(message, 'error');
            return of(LiveClassActions.createLiveClassFailed({ error: err }));
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  onCreateSucces = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveClassActions.createLiveClassSuccess.type),
        switchMap((action: { liveClass: LiveClass }) => {
          this.router.navigate([
            'live-classes/add-success',
            action.liveClass.id,
          ]);
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  fetchAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveClassActions.fetchAllLiveClasses.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.getAll(action.page).pipe(
          map((response) => {
            return LiveClassActions.fetchAllLiveClassesSuccess({
              response,
            });
          }),
          catchError((err) =>
            of(LiveClassActions.fetchAllLiveClassesFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  fetchAllForInstructor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveClassActions.fetchAllForInstructor.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.getAllForInstructor().pipe(
          map((response) => {
            return LiveClassActions.fetchAllLiveClassesSuccess({
              response: response,
              forInstructor: true,
            });
          }),
          catchError((err) =>
            of(LiveClassActions.fetchAllLiveClassesFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  restoreCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveClassActions.restoreLiveClassItem.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.getById(action.id).pipe(
          map((response) => {
            return LiveClassActions.restoreLiveClassItemSuccess({
              item: response,
            });
          }),
          catchError(() => {
            this.router.navigate(['/404']);
            return EMPTY;
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveClassActions.updateLiveClass.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService
          .update(action.liveClassId, action.liveClass)
          .pipe(
            map((response) => {
              return LiveClassActions.updateLiveClassSuccess({
                liveClass: response,
              });
            }),
            tap(() => {
              if (action.redirectTo) {
                this.router.navigate([action.redirectTo]);
              }
            }),
            catchError((err) => {
              const message =
                (typeof err?.error === 'string'
                  ? err.error
                  : err?.error?.message || err?.error?.description) ||
                err?.message ||
                'Failed to update live class.';
              this.toastService.showToast(message, 'error');
              return of(LiveClassActions.updateLiveClassFailed({ error: err }));
            }),
            finalize(() => this.pageLoader.close())
          );
      })
    )
  );

  selectOne$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.selectLiveClass.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.getById(action.id).pipe(
          map((response) =>
            LiveClassActions.selectLiveClassSuccess({ liveClass: response })
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  sendForReview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.requestForReview.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.requestForReview(action.liveClassId).pipe(
          map((response) => {
            return LiveClassActions.requestForReviewSuccess({
              liveClass: response,
            });
          }),
          catchError((err) => {
            const message =
              (typeof err?.error === 'string'
                ? err.error
                : err?.error?.message || err?.error?.description) ||
              err?.message ||
              'Failed to submit live class for review.';
            this.toastService.showToast(message, 'error');
            return of(LiveClassActions.requestForReviewFailed({ error: err }));
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  publishLiveClass$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.publishLiveClass.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService.publish(action.liveClassId).pipe(
          map((response) => {
            this.toastService.showToast(
              'Live class published successfully!',
              'success'
            );
            this.router.navigate(['/instructor']);
            return LiveClassActions.publishLiveClassSuccess({
              liveClass: response,
            });
          }),
          catchError((err) => {
            this.toastService.showToast(
              err?.error?.message || 'Failed to publish live class.',
              'error'
            );
            return of(LiveClassActions.publishLiveClassFailed({ error: err }));
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  subscribeToLiveClass = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.subscribeToLiveClass.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.liveClassService
          .subscribeToLiveClass(action.id, action.paymentType, action.targetEvent)
          .pipe(
            map((response) =>
              LiveClassActions.subscribeToLiveClassSuccess({
                invoice: response.unpaidInvoice,
                subscription: response.subscription,
              })
            ),
            catchError((err) =>
              of(LiveClassActions.subscribeToLiveClassFailed({ error: err }))
            ),
            finalize(() => this.pageLoader.close())
          );
      })
    );
  });

  processPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.processPayment.type),
      switchMap((action: { invoiceId: string; payment: Payment }) => {
        this.pageLoader.open();
        return this.liveClassService
          .processPayment(action.invoiceId, action.payment)
          .pipe(
            map((response) =>
              LiveClassActions.processSuccess({
                invoice: response,
              })
            ),
            catchError((err: HttpErrorResponse) => {
              return of(LiveClassActions.processPaymentFailed({ err }));
            }),
            finalize(() => this.pageLoader.close())
          );
      })
    );
  });

  fetchInvoice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.fetchInvoice.type),
      switchMap((action: { invoiceId: string }) => {
        return this.liveClassService.fetchInvoice(action.invoiceId).pipe(
          map((response) =>
            LiveClassActions.fetchInvoiceSuccess({
              invoice: response,
            })
          ),
          catchError((err: HttpErrorResponse) => {
            return of(LiveClassActions.fetchInvoiceFailed({ err }));
          })
        );
      })
    );
  });

  fetchAllSubscribedLiveClasses = createEffect(() => {
    return this.actions$.pipe(
      ofType(LiveClassActions.fetchSubscribedLiveClasses.type),
      switchMap((actions: any) => {
        this.pageLoader.open();
        return this.liveClassService.fetchAllSubscribedClasses().pipe(
          map((response) =>
            LiveClassActions.fetchSubscribedLiveClassesSuccess({
              subscribedLiveClasses: response,
            })
          ),
          catchError((err: HttpErrorResponse) => {
            return EMPTY; // TODO: handled error cases
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  subscribeToLiveClassSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveClassActions.subscribeToLiveClassSuccess.type),
        tap(
          (action: { invoice: Invoice | null; subscription: Subscription }) => {
            if (action.invoice) {
              // Redirect to invoice payment page if invoice is present
              this.router.navigate([
                'subscription/invoice',
                action.invoice._id,
                'pay',
              ]);
            } else if (
              action.subscription &&
              action.subscription.status === SubscriptionStatus.COMPLETE
            ) {
              // Redirect to my-learnings page if subscription is complete [free class]
              this.router.navigate(['/my-learnings']);
              this.toastService.showToast(
                'Successfully subscribed to the live class!',
                'success'
              );
            } else {
              this.toastService.showToast(
                'Subscription succeeded, but there was an issue retrieving payment details.',
                'warning'
              );
              this.router.navigate(['/my-learnings']);
            }
          }
        )
      ),
    { dispatch: false }
  );
}
